import {
  CopyTask,
  Icon,
  NewTask,
  BackgroundIcon,
  isAuthorised,
  AccessControl
} from '@nile/components';
import {
  Button,
  Collapsible,
  Heading,
  ButtonGroup
} from '@superops/ui-design-system';
import { Medal, Tasks } from '@styled-icons/fa-solid';
import {
  KeyboardArrowDown,
  KeyboardArrowUp
} from '@styled-icons/material-twotone';
import React, {
  useCallback,
  useState,
  useContext,
  useEffect,
  useRef,
  useMemo
} from 'react';
import styled from 'styled-components';
import envConfig from '@nile/home/src/Config/env';
import ProjectContext from '@nile/projects/src/Contexts/ProjectContext';
import { constructTaskObject } from '@nile/projects/src/Utils';
import TaskCard from './TaskStrip';
import { SVGIcon } from '@superops/super-icons';
import { SuperSortable } from '@nile/components/src/SuperSortable/SuperSortable';
import { sortBy } from 'lodash';

const CardWrapper = styled.div`
  .card-border {
    border: 1px solid #0000001a;
  }
  .br-10 {
    border-radius: 10px;
  }
  .mx--20 {
    margin-left: -20px;
    margin-right: -20px;
  }
`;

const OuterWrapper = styled.div`
  display: flex;
  flex: 1 1;
  align-items: end;

  &.secondCol {
    justify-content: flex-end;
  }

  .deleteBtn :hover {
    color: #e02020;
  }
  .toggleBtn {
    color: #00000066;
  }
`;

const OtherTasksCard = ({
  tasks,
  handleOtherTasksAddition,
  handleOtherTaskTemplateAddition,
  taskTemplates,
  module,
  hideSectionsInTaskForm = [],
  sendAllKeysInTemplate,
  isTemplate,
  getSelectedTaskNameList,
  preventScroll,
  onTasksReorder,
  updateOtherTasks
}) => {
  const [active, setActive] = useState(true);
  const [sheetActive, setSheetActive] = useState(false);
  const ref = useRef();

  const projectContext = useContext(ProjectContext);

  useEffect(() => {
    if (ref.current && !preventScroll) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, []);

  const handleToggle = useCallback(() => setActive((val) => !val), []);

  const toggleSheetActive = () => {
    setSheetActive(!sheetActive);
  };

  const handleTaskAddition = (type, task) => {
    const taskObj = constructTaskObject(task, hideSectionsInTaskForm);
    handleOtherTasksAddition(taskObj);
    setSheetActive(!sheetActive);
  };

  const filterAttrs = (obj) => {
    const { name, ...rest } = obj;
    return rest;
  };

  const updateContext = (type, updatedTasks) => {
    if (type === 'taskTemplateList') {
      const updatedTaskList = updatedTasks.map((taskTemp) => {
        if (sendAllKeysInTemplate) {
          return {
            ...constructTaskObject(taskTemp, hideSectionsInTaskForm),
            taskTemplateId: taskTemp.taskTemplateId
          };
        }
        const { taskTemplateId, ...rest } = taskTemp;
        if (rest.technician) {
          rest.technician = filterAttrs(rest.technician);
        }
        return rest;
      });
      handleOtherTaskTemplateAddition(updatedTaskList);
    }
  };

  const context = {
    value: {
      addTaskTemplateList: [],
      removeTaskTemplateList: [],
      taskTemplateList: [],
      selectedTaskTemplates: []
    },
    updateContext: updateContext
  };

  const modifiedTaskList =
    taskTemplates?.getTaskTemplateList?.map((task) => ({
      ...task,
      id: task.taskTemplateId,
      name: task.title,
      label: task.title
    })) || [];

  const tasksList = getSelectedTaskNameList
    ? modifiedTaskList?.map((task) => ({
        ...task,
        disable: getSelectedTaskNameList().includes(task.title)
      })) || []
    : modifiedTaskList;

  function handleTaskDeletion(ind) {
    const updatedTasks = [...tasks]
      .filter((_, i) => i !== ind)
      .map((item, i) => ({ ...item, taskOrder: i + 1 }));
    updateOtherTasks(updatedTasks);
  }

  function handleTaskUpdation(ind, task) {
    const taskObj = constructTaskObject(task, hideSectionsInTaskForm);
    const updatedTasks = [...tasks];
    updatedTasks[ind] = {
      ...taskObj,
      ...(isTemplate
        ? { taskTemplateId: updatedTasks[ind].taskTemplateId }
        : {})
    };

    updateOtherTasks(updatedTasks);
  }

  return (
    <CardWrapper className={active ? 'expanded' : ''} ref={ref}>
      <div className="card-border p20 br-10 mb-20 mx--20 visibilityContainer">
        <div className="fx">
          <OuterWrapper>
            <div className="opacity5">
              <SVGIcon name="Tasks" size="lg" />
            </div>
            <div className="ml-10">
              <Heading element="h2">Other tasks</Heading>
            </div>
          </OuterWrapper>
          <OuterWrapper className="secondCol">
            <ButtonGroup>
              <div className="visibilityActions" data-qa="createOtherTask">
                <NewTask
                  addOrUpdateTechGroup={handleTaskAddition}
                  state={sheetActive}
                  toggleSheetActive={toggleSheetActive}
                  name="Create Task"
                  icon={
                    <div className="opacity5">
                      <SVGIcon name="Tasks" />
                    </div>
                  }
                  hideIcon
                  module={module || 'PROJECT'}
                  hideSections={hideSectionsInTaskForm}
                  details={{ operation: 'CREATE' }}
                  isTemplate={isTemplate}
                />
              </div>
              <AccessControl
                roleToCheck="taskTemplate.view"
                render={
                  <div
                    className="visibilityActions"
                    data-qa="createOtherTaskTemplate"
                  >
                    <CopyTask
                      tasksList={tasksList}
                      context={context}
                      name="Task templates"
                      sendAll={sendAllKeysInTemplate}
                      usePropTemplates={!!getSelectedTaskNameList}
                      hideSectionsInTask={hideSectionsInTaskForm}
                      icon={
                        <div className="opacity5">
                          <SVGIcon name="Tasks" />
                        </div>
                      }
                      hideIcon
                    />
                  </div>
                }
              />
              <div className="visibilityActions toggleBtn">
                <Button
                  onClick={handleToggle}
                  plain
                  icon={
                    active ? (
                      <SVGIcon name="ArrowDown" size="lg" />
                    ) : (
                      <SVGIcon name="ArrowUp" size="lg" />
                    )
                  }
                />
              </div>
            </ButtonGroup>
          </OuterWrapper>
        </div>
        <Collapsible
          open={active}
          transition={{ duration: '150ms', timingFunction: 'ease' }}
        >
          <SuperSortable
            onDragEnd={(list) => {
              const newList = list.map((task, index) => ({
                ...task,
                taskOrder: index + 1
              }));

              onTasksReorder(newList);
            }}
            list={tasks}
          >
            {tasks.map((task, index) => (
              <TaskCard
                task={task}
                handleTaskDeletion={handleTaskDeletion}
                handleTaskUpdation={handleTaskUpdation}
                index={index}
                module={module}
                hideSectionsInTaskForm={hideSectionsInTaskForm}
                isTemplate={isTemplate}
                enableDrag
              />
            ))}
          </SuperSortable>
        </Collapsible>
      </div>
    </CardWrapper>
  );
};

export default OtherTasksCard;
